import type {
  MachineQuotationItemDto,
  MachineQuotationItemMarginDto,
  TradeInItemDto
} from '@api/machines'

import { calculateAndAssignItemPrices } from './calculatePricesForItems'

type CalculateQuotationItemsParams = {
  isFirstCalculation?: boolean
  machineAttachmentItems?: MachineQuotationItemMarginDto[] | null
  machineItems?: MachineQuotationItemDto[] | null
  recalculateSellPrice?: boolean
  resetSellPrice?: boolean
  tradeInItems?: TradeInItemDto[]
  excludeOverAllowance?: boolean
}

export const calculateQuotationItems = ({
  isFirstCalculation = true,
  machineAttachmentItems,
  machineItems,
  recalculateSellPrice = false,
  resetSellPrice = false,
  tradeInItems,
  excludeOverAllowance = false
}: CalculateQuotationItemsParams) => {
  if (!(machineAttachmentItems || machineItems || tradeInItems)) {
    return {
      combinedQuotationItems: [],
      machineAttachmentItems: [],
      machineItems: []
    }
  }

  const overAllowanceTotal =
    tradeInItems?.reduce(
      (acc: number, tradeIn: TradeInItemDto) =>
        acc + (tradeIn?.overAllowance ?? 0),
      0
    ) ?? 0

  const itemsToCalculateSum = [
    ...(machineItems ?? []),
    ...(machineAttachmentItems ?? [])
  ]

  const machineAndAttachmentTotal = itemsToCalculateSum.reduce((sum, item) => {
    const priceWithoutDnp =
      (item?.price ?? 0) -
      (item.serviceContract?.dnp ?? 0) -
      (item.factorySupport?.dnp ?? 0)

    return sum + priceWithoutDnp * (item?.quantity ?? 1)
  }, 0)

  const calculatedMachineAttachments = calculateAndAssignItemPrices({
    isFirstCalculation,
    items: machineAttachmentItems ?? [],
    machineAndAttachmentTotal,
    overAllowanceTotal,
    recalculateSellPrice,
    resetSellPrice,
    excludeOverAllowance
  })

  const calculatedMachines = calculateAndAssignItemPrices({
    isFirstCalculation,
    items: machineItems ?? [],
    machineAndAttachmentTotal,
    overAllowanceTotal,
    recalculateSellPrice,
    resetSellPrice,
    excludeOverAllowance
  })

  const combinedQuotationItems = [
    ...calculatedMachines,
    ...calculatedMachineAttachments
  ]

  return {
    combinedQuotationItems,
    machineAttachmentItems: calculatedMachineAttachments,
    machineItems: calculatedMachines
  }
}
